import moment from 'moment';
import cronstrue from 'cronstrue';
import { parseExpression } from 'cron-parser';
import {
    CARD_STATES,
    isPausedStatus,
    MONITOR_STATUS,
    RUN_FINISH_REASONS,
    SCHEDULE_FREQUENCIES,
    VERDICT_OUTCOMES,
} from './constants';

export const formatWeekDayLabel = day =>
    moment()
        .isoWeekday(day)
        .format('ddd');

export const presetToCron = ({ frequency, days, hour, minute = 0 }) => {
    if (frequency === SCHEDULE_FREQUENCIES.HOURLY) return '0 * * * *';
    if (frequency === SCHEDULE_FREQUENCIES.DAILY) return `${minute} ${hour} * * *`;
    const orderedDays = [...days].sort((a, b) => a - b);
    return `${minute} ${hour} * * ${orderedDays.map(day => day % 7).join(',')}`;
};

export const cronToPreset = cron => {
    let fields;
    try {
        ({ fields } = parseExpression(cron ?? ''));
    } catch {
        return null;
    }
    if (fields.minute.length !== 1) return null;
    if (fields.dayOfMonth.length !== 31 || fields.month.length !== 12) return null;
    const [minute] = fields.minute;
    const daySet = new Set(fields.dayOfWeek.map(dow => ((dow + 6) % 7) + 1));
    const everyDay = daySet.size === 7;
    if (fields.hour.length === 24) {
        return everyDay ? { frequency: SCHEDULE_FREQUENCIES.HOURLY, days: [], hour: 0, minute } : null;
    }
    if (fields.hour.length !== 1) return null;
    const [hour] = fields.hour;
    if (everyDay) return { frequency: SCHEDULE_FREQUENCIES.DAILY, days: [], hour, minute };
    return {
        frequency: SCHEDULE_FREQUENCIES.WEEKLY,
        days: [...daySet].sort((a, b) => a - b),
        hour,
        minute,
    };
};

export const getScheduleSummary = ({ frequency, days, hour, minute = 0 }, slackChannelName = null) => {
    const time = moment({ hour, minute }).format('hh:mm A');
    const suffix = slackChannelName ? '_SLACK' : '';
    const channel = slackChannelName ? { channel: slackChannelName } : {};
    if (frequency === SCHEDULE_FREQUENCIES.HOURLY) return { key: `SUMMARY_HOURLY${suffix}`, data: { ...channel } };
    if (frequency === SCHEDULE_FREQUENCIES.DAILY) return { key: `SUMMARY_DAILY${suffix}`, data: { time, ...channel } };
    if (!days.length) return { key: `SUMMARY_WEEKLY_NONE${suffix}`, data: {} };
    if (days.length === 1) {
        return {
            key: `SUMMARY_WEEKLY_SINGLE${suffix}`,
            data: {
                time,
                day: moment()
                    .isoWeekday(days[0])
                    .format('dddd'),
                ...channel,
            },
        };
    }
    return { key: `SUMMARY_WEEKLY${suffix}`, data: { time, dayCount: days.length, ...channel } };
};

export const formatScheduleLabel = cron => {
    try {
        return cronstrue.toString(cron);
    } catch {
        return cron;
    }
};

export const formatShortScheduleLabel = cron => {
    const preset = cronToPreset(cron);
    if (!preset) return { label: formatScheduleLabel(cron), title: null };
    const { frequency, days, hour, minute } = preset;
    if (frequency === SCHEDULE_FREQUENCIES.HOURLY) return { label: 'Checks every hour', title: null };
    const time = moment({ hour, minute }).format('h:mm A');
    if (frequency === SCHEDULE_FREQUENCIES.DAILY || days.length === 7) {
        return { label: `Checks daily at ${time}`, title: null };
    }
    const dayNames = days.map(formatWeekDayLabel);
    const fullDayList = dayNames.join(', ');
    const consecutive = days.every((day, i) => i === 0 || day === days[i - 1] + 1);
    if (consecutive && days.length >= 3) {
        return { label: `Checks ${dayNames[0]} - ${dayNames[dayNames.length - 1]} at ${time}`, title: fullDayList };
    }
    if (days.length <= 3) return { label: `Checks ${fullDayList} at ${time}`, title: null };
    return { label: `Checks ${days.length} days a week at ${time}`, title: fullDayList };
};

// The composer states the schedule on its own, without the card's "Checks" lead-in. A cron
// the presets cannot express has no key and falls back to the raw cron description.
export const getComposerScheduleSummary = cron => {
    const preset = cronToPreset(cron);
    if (!preset) return null;
    const { frequency, days, hour, minute } = preset;
    if (frequency === SCHEDULE_FREQUENCIES.HOURLY) return { key: 'HOURLY', data: {} };
    const time = moment({ hour, minute }).format('h:mm A');
    if (frequency === SCHEDULE_FREQUENCIES.DAILY || days.length === 7) return { key: 'DAILY', data: { time } };
    if (days.length === 1) {
        return {
            key: 'SINGLE_DAY',
            data: {
                time,
                day: moment()
                    .isoWeekday(days[0])
                    .format('dddd'),
            },
        };
    }
    return { key: 'DAYS', data: { time, days: days.map(formatWeekDayLabel).join(', ') } };
};

// next_run_at arrives in the org's report timezone (the timezone the schedule runs
// in) - parseZone keeps that wall-clock time instead of shifting it to browser time.
export const formatNextRunLabel = nextRunAt =>
    nextRunAt ? moment.parseZone(nextRunAt).format('MMM D [at] h:mm A') : null;

export const getCardState = (monitor, { liveRuns, streamingSessionIds }) => {
    if (isPausedStatus(monitor.status)) return CARD_STATES.PAUSED;
    const run = liveRuns[monitor.session_id];
    const finishedLocally = run != null && !run.live && run.runId === monitor.active_run_id;
    const pausedForReply =
        finishedLocally && run.finishReason != null && run.verdict?.outcome === VERDICT_OUTCOMES.PROMPT_UNCLEAR;
    if (pausedForReply) return CARD_STATES.PAUSED;
    if (run?.live || (monitor.status === MONITOR_STATUS.RUNNING && !finishedLocally)) return CARD_STATES.RUNNING;
    if (monitor.status === MONITOR_STATUS.QUEUED) return CARD_STATES.QUEUED;
    if (streamingSessionIds.includes(monitor.session_id)) return CARD_STATES.BUSY;
    return CARD_STATES.VERDICT;
};

const getVerdictlessRunFallback = (finishReason, previousVerdict) => {
    switch (finishReason) {
        case RUN_FINISH_REASONS.STOP:
            return { outcome: VERDICT_OUTCOMES.NO_VERDICT };
        case RUN_FINISH_REASONS.ERROR:
            return { outcome: VERDICT_OUTCOMES.ERROR };
        case RUN_FINISH_REASONS.CANCELLED:
        default:
            return previousVerdict;
    }
};

export const getDisplayVerdict = (monitor, liveRun) => {
    const previousVerdict = monitor.latest_verdict ?? null;
    if (!liveRun?.finishReason) return previousVerdict;
    if (liveRun.verdict) return liveRun.verdict;
    return getVerdictlessRunFallback(liveRun.finishReason, previousVerdict);
};
